import { datadogRum } from "@datadog/browser-rum";

import type { ILocation, IStall } from "../contexts/ConfigContext/types";
import { LINKPAY_COOKIES } from "../pages/constants";
import { getOrCreateFingerprint } from "./fingerprint";

const getCookie = (name: string): string | null => {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);

	if (parts.length === 2) {
		const cookieValue = parts.pop()?.split(";").shift() || null;

		return cookieValue ? decodeURIComponent(cookieValue) : null;
	}

	return null;
};

const getCookieSubstring = (cookieName: string, maxLength: number = 10): string => {
	const cookieValue = getCookie(cookieName);

	return cookieValue ? cookieValue.substring(0, maxLength) : "";
};

interface DatadogConfig {
	applicationId: string;
	clientToken: string;
	site?: string;
	sessionReplaySampleRate?: number;
	env?: string;
	service?: string;
	version?: string;
}

export const initializeDatadogRUM = async (config: DatadogConfig): Promise<void> => {
	const fingerprint = await getOrCreateFingerprint();
	const {
		applicationId,
		clientToken,
		site = "datadoghq.eu", // EU endpoint
		sessionReplaySampleRate = 100,
		env = import.meta.env.MODE ?? "development",
		service = "sok",
		version = import.meta.env.VITE_APP_VERSION ?? "1.0.0",
	} = config;

	datadogRum.init({
		applicationId,
		clientToken,
		site,
		service,
		env,
		version,
		sessionReplaySampleRate,
		sessionSampleRate: 100,
		trackResources: true,
		trackLongTasks: true,
		trackUserInteractions: true,
		defaultPrivacyLevel: "mask-user-input",
		allowedTracingUrls: [
			{
				match: /https:\/\/.*\.fairpricegroup\.sg\/.*/,
				propagatorTypes: ["datadog", "tracecontext"],
			},
			{
				match: /https:\/\/.*\.sok\.com\.sg\/.*/,
				propagatorTypes: ["datadog", "tracecontext"],
			},
		],
	});

	datadogRum.startSessionReplayRecording();

	// Initialize user with device fingerprint and fp app cookies
	try {
		const fpAppCookies = {
			fp_access_token: getCookieSubstring(LINKPAY_COOKIES.ACCESS_TOKEN),
			fp_uid: getCookieSubstring(LINKPAY_COOKIES.UID),
			fp_has_default_card: getCookieSubstring(LINKPAY_COOKIES.HAS_DEFAULT_CARD),
		};

		setDatadogUser({
			id: fingerprint,
			device_fingerprint: fingerprint,
			device_type: "web",
			...fpAppCookies,
		});
	} catch (error) {
		console.error("Failed to set Datadog user with fingerprint:", error);
	}
};

export const setDatadogUser = (user: {
	id?: string;
	name?: string;
	email?: string;
	[key: string]: any;
}): void => {
	if (user.id) {
		datadogRum.setUser({
			id: user.id,
			name: user.name,
			email: user.email,
			...user,
		});
	}
};

export const clearDatadogUser = (): void => {
	datadogRum.clearUser();
};

export const startNewDatadogSession = (): void => {
	datadogRum.stopSession();
};

export const addDatadogAction = (name: string, context?: Record<string, any>): void => {
	datadogRum.addAction(name, context);
};

export const addDatadogError = (error: Error, context?: Record<string, any>): void => {
	datadogRum.addError(error, context);
};

export const setDatadogGlobalContext = (key: string, value: any): void => {
	datadogRum.setGlobalContextProperty(key, value);
};

export const removeDatadogGlobalContext = (key: string): void => {
	datadogRum.removeGlobalContextProperty(key);
};

export const getLocalStorageContext = (): Record<string, any> => {
	const localStorageData: Record<string, any> = {};

	try {
		const keys = ["PRODUCT_LIST_SCROLL", "VIEW_ONLY_MODE_DISMISSED"];

		keys.forEach((key) => {
			const value = localStorage.getItem(key);

			if (value !== null) {
				try {
					localStorageData[key] = JSON.parse(value);
				} catch {
					localStorageData[key] = value;
				}
			}
		});
	} catch (error) {
		console.warn("Failed to read localStorage data for Datadog context:", error);
	}

	return localStorageData;
};

export const addDatadogActionWithContext = (
	name: string,
	context?: Record<string, any>,
	includePaymentReference?: string,
	includeLocalStorage?: boolean
): void => {
	const enrichedContext = { ...context };

	if (includePaymentReference) {
		enrichedContext.paymentReference = includePaymentReference;
	}

	if (includeLocalStorage) {
		enrichedContext.localStorage = getLocalStorageContext();
	}

	datadogRum.addAction(name, enrichedContext);
};

export interface IDatadogLocationContext {
	stallId?: string;
	outletid?: string;
	stallName?: string;
}

// Shared helper used by per-page instrumentation to attach business location
// context (stallId / outletid / stallName) to RUM actions. Falls back to
// location.name when stall.name is unavailable so dashboards still get a label.
export const buildLocationContext = (
	location?: Pick<ILocation, "id" | "name" | "outletid"> | null,
	stall?: Pick<IStall, "name"> | null
): IDatadogLocationContext => {
	return {
		stallId: location?.id,
		outletid: location?.outletid,
		stallName: stall?.name ?? location?.name,
	};
};

export const addDatadogPageView = (
	pageName: string,
	paymentReference?: string,
	additionalContext?: Record<string, any>
): void => {
	const context = {
		page: pageName,
		localStorage: getLocalStorageContext(),
		...additionalContext,
	};

	if (paymentReference) {
		context.paymentReference = paymentReference;
	}

	datadogRum.addAction("page_view", context);
};
